
<template>
    <!-- FORMULAIRE -->
    <b-form 
        class="h-100 overflow-hidden"
        @reset="onReset">
        <b-card no-body class="d-flex h-100">
            <!-- COMPOSANT DE GESTION D'ETAPES FORMULAIRE -->
            <GenericStepper 
                @cancel="cancel"
                @submit="onSubmit"
                Title="Création d'un mandat" 
                :Steps="steps" 
                :CurrentStep.sync="currentStep" 
                :Valid="currentStep == metersStepIndex && certificate.MeterInformations.Meters.length > 0">
                <!-- VOIR POUR BLOQUER LA VALIDATION DU FORMULAIRE SI PAS DE COMPTEUR -->
                <!-- CHANGER LA VALIDATION CAR C'EST NUL MAIS PAS TROUVE DE SOLUTION POUR LE MOMENT -->
                <template v-slot:step-1>
                    <!-- ETAPE 1 - COMPOSANT SELECTION DISTRIBUTEUR-->
                    <DistributorForm 
                        :Certificate.sync="certificate" 
                        :Forms="Forms" 
                        :Mode="Mode"/>
                </template>
                <template v-slot:step-2>
                    <!-- ETAPE 2 - CONTENU SUIVANT LE TYPE D'ETAPE DECRITE PAR LE SERVEUR -->
                    <ClientForm 
                        v-if="formSteps[1] == formStepEnum.Customer"
                        :Certificate.sync="certificate" 
                        :Form="Forms[certificate.Distributor]"
                        :Mode="Mode"/>
                    <MetersForm 
                        v-if="formSteps[1] == formStepEnum.Meters"
                        :Certificate.sync="certificate" 
                        :Form="Forms[certificate.Distributor]" 
                        @OnRefreshMeter="OnRefreshMeter"/>
                </template>
                <template v-slot:step-3>
                    <!-- ETAPE 3 - CONTENU SUIVANT LE TYPE D'ETAPE DECRITE PAR LE SERVEUR -->
                    <MetersForm 
                        v-if="formSteps[2] == formStepEnum.Meters"
                        :Certificate.sync="certificate" 
                        :Form="Forms[certificate.Distributor]" 
                        @OnRefreshMeter="OnRefreshMeter"/>
                    <AddResource
                        v-if="formSteps[2] == formStepEnum.Appendixes"
                        :types="['.pdf']"
                        :max-file-size="3 * 1024 * 1024"
                        :Files.sync="certificate.Resources"/>
                </template>
                <template v-slot:step-4>
                    <!-- ETAPE 4 - COMPOSANT AJOUT RESSOURCE -->
                    <AddResource
                        v-if="formSteps[3] == formStepEnum.Appendixes"
                        :types="['.pdf']"
                        :max-file-size="3 * 1024 * 1024"
                        :Files.sync="certificate.Resources"/>
                </template>
            </GenericStepper>
        </b-card>
    </b-form>
</template>
<script lang="ts">

    /**
     * Composants Vue js
     */
    import { Component, InjectReactive, Prop, PropSync, Vue } from 'vue-property-decorator';
    import { mixins } from 'vue-class-component';

    /**
     * Composant Valideur formulaire
     */
    import { ValidationObserver } from 'vee-validate';

    /**
     * Classes et énumérations
     */
    import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';
    import { SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
import SMAFormStepEnum from '@/enums/Administration/SMA/SMAFormStepEnum';
    
    /**
     * Classe utilitaire
     */
    import Dictionary from '@/types/Dictionary';

    /**
     * Composants formulaire mandat
     */
    import DistributorForm from './DistributorForm/DistributorForm.vue';
    import ClientForm from './DistributorForm/ClientForm.vue';
    import MetersForm from './MetersForm.vue';
    import { SMAUtils } from './SMAUtils';
    import AddResource from './AddResource.vue';

    /**
     * Composant gestionnaire d'étapes
     */
    import GenericStepper from '@/components/commons/Generics/GenericStepper.vue';
    
    /**
     * Traductions
     */
    import locale from '../locales/SMA.json';

	@Component({
		components: {
            ClientForm,
            MetersForm,
            DistributorForm,
            ValidationObserver,
            GenericStepper,
            AddResource
        },
        i18n: {messages: locale}
	})
    /**
     * Formulaire des mandats distributeurs
     */
	export default class CertificateForm extends mixins(Vue, SMAUtils) {

        /**
         * Types d'étapes du formulaire mandat décrites par le serveur
         */
        public formStepEnum = SMAFormStepEnum;

        /**
         * Mandat
         */
        @PropSync('Certificate', { default: () => {} })
        public certificate!: SMACertificate;

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * Formulaires distributeurs
         */
        @Prop()
        public Forms!: Dictionary<SMADistributorForm>;

        /**
         * Mode du formulaire ("New" pour nouveau mandat, "Edit" pour modification)
         */
        @Prop()
        public Mode!: "New" | "Edit" | "Renew";

        /**
         * Machine d'états du formulaire : étapes du mandat décrites par le serveur (SMAAttribute FormSteps)
         * Le client se contente de rendre les étapes décrites, dans l'ordre déclaré
         */
        public get formSteps() : number[]
        {
            return this.Forms[this.certificate.Distributor]?.FormSteps
                ?? [SMAFormStepEnum.Distributor, SMAFormStepEnum.Customer, SMAFormStepEnum.Meters, SMAFormStepEnum.Appendixes];
        }

        /**
         * Libellés des étapes du formulaire
         */
        private get stepLabels() : { [key: number]: { title: string, description: string } }
        {
            return {
                [SMAFormStepEnum.Distributor]: { title: this.$t("Distributor").toString(), description: this.$t("DistributorFormDescription").toString() },
                [SMAFormStepEnum.Customer]: { title: this.$t("Customer").toString(), description: this.$t("CustomerFormDescription").toString() },
                [SMAFormStepEnum.Meters]: { title: this.$t("Meters").toString(), description: this.$t("MetersFormDescription").toString() },
                [SMAFormStepEnum.Appendixes]: { title: this.$t("Appendixes").toString(), description: this.$t("AppendixesFormDescription").toString() }
            };
        }

        /**
         * Etapes du formulaire construites depuis la description serveur
         */
        public get steps() : { title: string, description: string }[]
        {
            return this.formSteps
                .map(step => this.stepLabels[step])
                .filter(label => label != null);
        }

        /**
         * Indice de l'étape de sélection des compteurs
         */
        private get metersStepIndex() : number
        {
            return Math.max(this.formSteps.indexOf(SMAFormStepEnum.Meters), 0);
        }

        /** 
         * Etape courante
         */
        public currentStep: number = 0;

        /**
         * Submit du formulaire
         */
        private onSubmit() : void {
            this.$store.dispatch('CertificateStore/Save', { item: this.certificate, action: this.Mode == "Renew" ? "Renew" : "Save" }).then((data) => 
            {
                this.showToast(data.message, data.success ? "success" : "danger", "SaveComplete");
                this.$emit('Validation'); // => Emission evenement pour fermer le formulaire
            })
        }

        /**
         * Rafraichissement des données du compteur
         * Utilisé si une information du compteur est incorrect (Exemple : RAE, Adresse, Energie)
         * Ce méthode va mettre à jour les informations du compteur suivant les données de la fiche objet
         * @param Id Identifiant du compteur
         */
        private OnRefreshMeter(Id: string) : void
        {
            this.$emit('OnRefreshMeter', Id);
        }

        /**
         * Reset du formulaire
         */
        private onReset() : void 
        {
            this.$emit('Cancel');
        }

        /**
         * Event d'annulation du formulaire
         */
        private cancel() : void 
        {
            this.$emit('Cancel');
        }

        /**
         * Montée en charge du composant
         */
        mounted(){            
            // Si Mode création (nouveau mandat) on commence à la premiere étape 
            // Sinon on passe directement à l'étape 2 pour les modifications
            this.currentStep = this.Mode == "New" ? 0 : 1;
        }
    }
    
</script>
<style scoped>
</style>